/**
 * % By State Distribution — whole-percent allocation.
 *
 * `allocateWholePercents` converts a list of exposures into integer
 * percentages that always sum to exactly 100 (largest-remainder
 * method). Rounding each share independently can drift the total to
 * 99 or 101 (OR-1027), which reads as a bug next to a progress bar.
 */
export function allocateWholePercents(values: number[]): number[] {
    const safe = values.map((v) => (Number.isFinite(v) && v > 0 ? v : 0));
    const total = safe.reduce((sum, v) => sum + v, 0);
    if (total <= 0) return safe.map(() => 0);

    const exact = safe.map((v) => (v / total) * 100);
    const out = exact.map(Math.floor);
    let leftover = 100 - out.reduce((sum, v) => sum + v, 0);

    // Hand the leftover points to the largest fractional remainders;
    // ties break by index so the result is deterministic.
    const byRemainder = exact
        .map((v, i) => ({ i, remainder: v - Math.floor(v) }))
        .sort((a, b) => b.remainder - a.remainder || a.i - b.i);
    for (let k = 0; k < byRemainder.length && leftover > 0; k += 1, leftover -= 1) {
        out[byRemainder[k].i] += 1;
    }
    return out;
}
